// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import cdlogo from "../assets/cdlogo.png"; // Use your black logo

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Pages that use a light background at the top
  const isLightPage = location.pathname === "/careers";
  const isDarkNav = !scrolled && !isLightPage;

  const menuLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Service", path: "/services" },
    { name: "Products", path: "/product" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleTalkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/contact");
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed w-full z-40 flex justify-between items-center py-4 px-6 md:px-12 lg:px-20 transition-all duration-300 ${
        isDarkNav
          ? "bg-transparent text-white"
          : "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200/80 text-black"
      }`}
    >
      {/* Logo */}
      <div className="flex items-center space-x-2">
        <Link to="/" onClick={handleNavClick}>
          <img
            src={cdlogo}
            alt="Crack Developers Logo"
            className={`w-32 transition-all duration-300 ${
              isDarkNav ? "filter brightness-0 invert" : "filter brightness-0"
            }`}
          />
        </Link>
      </div>

      {/* Desktop Menu */}
      <div className="hidden lg:flex space-x-10 font-medium">
        {menuLinks.map((link) => (
          <Link
            key={link.name}
            to={link.path}
            onClick={handleNavClick}
            className={`transition duration-300 ${
              isDarkNav
                ? "text-gray-200 hover:text-lime-300"
                : "text-gray-800 hover:text-black font-semibold"
            }`}
          >
            {link.name}
          </Link>
        ))}
      </div>

      {/* Contact Button */}
      <div className="hidden lg:block">
        <button
          onClick={handleTalkClick}
          className={`px-5 py-2 rounded-lg font-semibold border transition duration-300 ${
            isDarkNav
              ? "border-white text-white hover:text-lime-300 hover:border-lime-300"
              : "border-black text-black hover:bg-black hover:text-white"
          }`}
        >
          Let’s Talk
        </button>
      </div>

      {/* Mobile Menu Icon */}
      <div className="lg:hidden flex items-center">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setMenuOpen(!menuOpen)}
          className="focus:outline-none"
        >
          {menuOpen ? (
            <X
              className={`w-7 h-7 ${isDarkNav ? "text-white" : "text-black"}`}
            />
          ) : (
            <Menu
              className={`w-7 h-7 ${isDarkNav ? "text-white" : "text-black"}`}
            />
          )}
        </motion.button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="absolute top-full left-0 w-full bg-white shadow-xl border-b border-gray-200 lg:hidden"
          >
            <div className="flex flex-col items-center space-y-6 py-8 font-semibold text-gray-900">
              {menuLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={handleNavClick}
                  className="hover:text-black transition"
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={handleTalkClick}
                className="mt-4 px-6 py-2 border border-black text-black rounded-lg hover:bg-black hover:text-white transition"
              >
                Let’s Talk
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
